import { useState } from "react";
import axios from "axios";
import { Loader2, Send, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import PageShell from "@/components/site/PageShell";
import PageHero from "@/components/site/PageHero";
import { usePageContent } from "@/i18n/pageContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EU_COUNTRIES = [
  "Austria", "Belgium", "Bulgaria", "Croatia", "Cyprus", "Czech Republic",
  "Denmark", "Estonia", "Finland", "France", "Germany", "Greece",
  "Hungary", "Ireland", "Italy", "Latvia", "Lithuania", "Luxembourg",
  "Malta", "Netherlands", "Poland", "Portugal", "Romania", "Slovakia",
  "Slovenia", "Spain", "Sweden",
];

export default function NPTApply() {
  const c = usePageContent("nptApply");
  const [form, setForm] = useState({
    name: "", email: "", country: "", phone: "", profession: "", motivation: "",
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/npt-application`, form);
      setStatus({ state: "success", message: c.success });
      setForm({ name: "", email: "", country: "", phone: "", profession: "", motivation: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || c.errorGeneric;
      setStatus({ state: "error", message: typeof detail === "string" ? detail : c.errorGeneric });
    }
  };

  return (
    <PageShell>
      <PageHero
        eyebrow={c.eyebrow}
        title={<>{c.titleA} <span className="italic">{c.titleB}</span>.</>}
        subtitle={c.subtitle}
      />
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link to="/national-project-team" className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.28em] uppercase text-[#475569] hover:text-[#1a365d] mb-8">
            <ArrowLeft size={12} /> {c.backLink}
          </Link>
          <form onSubmit={submit} className="bg-[#fafaf7] p-8 md:p-12 border-l-2 border-[#d4af37]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <label className="block">
                <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">{c.name}</span>
                <input type="text" required minLength={2} value={form.name} onChange={set("name")} data-testid="npt-name"
                  className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body" />
              </label>
              <label className="block">
                <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">{c.email}</span>
                <input type="email" required value={form.email} onChange={set("email")} data-testid="npt-email"
                  className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body" />
              </label>
              <label className="block">
                <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">{c.country}</span>
                <select required value={form.country} onChange={set("country")} data-testid="npt-country"
                  className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body">
                  <option value="">{c.countryPlaceholder}</option>
                  {EU_COUNTRIES.map((cc) => <option key={cc} value={cc}>{cc}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">{c.phone} <span className="normal-case text-[#475569]/60">{c.phoneOptional}</span></span>
                <input type="tel" value={form.phone} onChange={set("phone")} data-testid="npt-phone"
                  className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body" />
              </label>
            </div>
            <label className="block mt-8">
              <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">{c.profession}</span>
              <input type="text" value={form.profession} onChange={set("profession")} data-testid="npt-profession"
                className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body" />
            </label>
            <label className="block mt-8">
              <span className="text-[10.5px] tracking-[0.32em] uppercase text-[#475569]">{c.motivation}</span>
              <textarea required minLength={10} rows={6} value={form.motivation} onChange={set("motivation")} data-testid="npt-motivation"
                placeholder={c.motivationPlaceholder}
                className="mt-2 w-full bg-transparent border-b border-[#1a365d]/30 focus:border-[#d4af37] outline-none py-2 text-[#1a365d] font-body resize-none placeholder:text-[#475569]/50" />
            </label>
            <button type="submit" disabled={status.state === "loading"} data-testid="npt-submit"
              className="btn-primary mt-10 disabled:opacity-60 disabled:cursor-not-allowed">
              {status.state === "loading" ? (<><Loader2 size={14} className="animate-spin" /> {c.submitting}</>) : (<>{c.submit} <Send size={14} strokeWidth={1.8} /></>)}
            </button>
            {status.message && (
              <p data-testid="npt-feedback" className={`mt-5 text-sm font-body ${status.state === "success" ? "text-[#1a365d]" : "text-[#9b1c1c]"}`}>
                {status.message}
              </p>
            )}
          </form>
        </div>
      </section>
    </PageShell>
  );
}
